import { useState } from "react";
import {
  BarChart3,
  Bookmark,
  Eye,
  Forward,
  Heart,
  MessageCircle,
  MessageSquare,
  Percent,
  TrendingDown,
  TrendingUp,
  Users,
  Zap,
} from "lucide-react";
import { Card } from "@/components/ui/Card";
import { Loader } from "@/components/ui/Loader";
import { formatNumber, formatPercent } from "@/utils/format";
import { cn } from "@/utils/cn";
import type {
  InstagramAccountInfo,
  InstagramFollowersSnapshot,
  InstagramIndicators,
  InstagramKpis,
  InstagramPublishedPerformance,
} from "@/services/marketing";
import { InstagramAvatar } from "./instagramImage";

type MetricsTab = "account" | "published";

interface Props {
  kpis: InstagramKpis | null | undefined;
  followers: InstagramFollowersSnapshot | null | undefined;
  indicators: InstagramIndicators | undefined;
  published: InstagramPublishedPerformance | null | undefined;
  loading?: boolean;
  username?: string;
  accounts?: InstagramAccountInfo[];
  selectedAccount?: string;
}

const ACCOUNT_METRICS = [
  { key: "likes" as const, label: "Curtidas", icon: Heart, color: "text-pink-500" },
  { key: "comments" as const, label: "Comentários", icon: MessageCircle, color: "text-indigo-500" },
  { key: "shares" as const, label: "Encaminhamentos", icon: Forward, color: "text-amber-500" },
  { key: "saves" as const, label: "Salvamentos", icon: Bookmark, color: "text-emerald-500" },
  { key: "replies" as const, label: "Respostas", icon: MessageSquare, color: "text-violet-500" },
  { key: "views" as const, label: "Visualizações", icon: Eye, color: "text-orange-500" },
  { key: "reach" as const, label: "Alcance", icon: Users, color: "text-sky-500" },
];

const PUBLISHED_METRICS = [
  { key: "likes" as const, label: "Curtidas", icon: Heart, color: "text-pink-500" },
  { key: "comments" as const, label: "Comentários", icon: MessageCircle, color: "text-indigo-500" },
  { key: "shares" as const, label: "Encaminh.", icon: Forward, color: "text-amber-500" },
  { key: "saves" as const, label: "Salvamentos", icon: Bookmark, color: "text-emerald-500" },
  { key: "views" as const, label: "Visualizações", icon: Eye, color: "text-orange-500" },
  { key: "reach" as const, label: "Alcance Σ", icon: Users, color: "text-sky-500" },
] as const;

const TABS: { id: MetricsTab; label: string; hint: string }[] = [
  {
    id: "account",
    label: "Conta geral",
    hint: "Igual ao Insights: interações no intervalo em qualquer publicação (inclui posts antigos e respostas de Stories)",
  },
  {
    id: "published",
    label: "Publicações do período",
    hint: "Métricas acumuladas dos posts/Reels publicados no filtro (lifetime, inclui engajamento posterior)",
  },
];

function MetricsGrid<T extends string>({
  items,
  data,
}: {
  items: readonly { key: T; label: string; icon: typeof Heart; color: string }[];
  data: Record<T, number> | null | undefined;
}) {
  return (
    <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-7">
      {items.map(({ key, label, icon: Icon, color }) => (
        <div
          key={key}
          className="flex items-center gap-2 rounded-lg border border-slate-100 bg-slate-50 px-3 py-2.5"
        >
          <Icon className={`h-4 w-4 flex-shrink-0 ${color}`} />
          <div className="min-w-0">
            <p className="truncate text-[10px] font-semibold uppercase tracking-wide text-slate-500">
              {label}
            </p>
            <p className="text-sm font-bold tabular-nums text-slate-800">
              {formatNumber(data?.[key] ?? 0)}
            </p>
          </div>
        </div>
      ))}
    </div>
  );
}

function IndicatorCards({
  indicators,
  interactions,
  loading,
  variant,
  published,
}: {
  indicators: InstagramIndicators | undefined;
  interactions: number;
  loading?: boolean;
  variant: MetricsTab;
  published?: InstagramPublishedPerformance | null;
}) {
  if (variant === "published" && published) {
    return (
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <Card
          title="Interações"
          value={loading ? undefined : formatNumber(published.interactions)}
          subtitle={`${published.feedCount} Feed · ${published.reelsCount} Reels`}
          icon={<Zap className="h-5 w-5" />}
          infoHint="Soma lifetime de curtidas, comentários, encaminhamentos e salvamentos dos posts publicados no período"
          loading={loading}
          className="border-l-4 border-l-violet-500"
        />
        <Card
          title="Taxa de engajamento"
          value={loading ? undefined : formatPercent(published.indicators.engagementRate, 2)}
          subtitle="Sobre alcance somado"
          icon={<Heart className="h-5 w-5" />}
          infoHint="Alcance Σ pode contar a mesma conta em posts diferentes"
          loading={loading}
          className="border-l-4 border-l-pink-500"
        />
        <Card
          title="Views / alcance"
          value={loading ? undefined : formatNumber(published.indicators.viewsPerReach, 2)}
          subtitle="Média por conta (Σ)"
          loading={loading}
          className="border-l-4 border-l-orange-500"
        />
        <Card
          title="Curtidas / 1k alcance"
          value={loading ? undefined : formatNumber(published.indicators.likesPer1kReach, 1)}
          subtitle="Eficiência dos posts"
          loading={loading}
          className="border-l-4 border-l-rose-500"
        />
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5">
      <Card
        title="Interações"
        value={loading ? undefined : formatNumber(interactions)}
        subtitle="Total no período"
        icon={<Zap className="h-5 w-5" />}
        infoHint="Igual ao Insights: curtidas + comentários + encaminhamentos + salvamentos + respostas"
        loading={loading}
        className="border-l-4 border-l-violet-500"
      />
      <Card
        title="Taxa de engajamento"
        value={loading ? undefined : formatPercent(indicators?.engagementRate ?? 0, 2)}
        subtitle="Interações ÷ alcance"
        icon={<Percent className="h-5 w-5" />}
        infoHint="Interações oficiais ÷ alcance × 100"
        loading={loading}
        className="border-l-4 border-l-pink-500"
      />
      <Card
        title="Views / alcance"
        value={loading ? undefined : formatNumber(indicators?.viewsPerReach ?? 0, 2)}
        subtitle="Média de visualizações por conta"
        icon={<BarChart3 className="h-5 w-5" />}
        loading={loading}
        className="border-l-4 border-l-orange-500"
      />
      <Card
        title="Curtidas / 1k alcance"
        value={loading ? undefined : formatNumber(indicators?.likesPer1kReach ?? 0, 1)}
        subtitle="Eficiência de curtidas"
        icon={<Heart className="h-5 w-5" />}
        loading={loading}
        className="border-l-4 border-l-rose-500"
      />
      <Card
        title="Taxa de salvamento"
        value={loading ? undefined : formatPercent(indicators?.saveRate ?? 0, 2)}
        subtitle="Salvamentos ÷ alcance"
        icon={<Bookmark className="h-5 w-5" />}
        loading={loading}
        className="border-l-4 border-l-emerald-500"
      />
    </div>
  );
}

function AccountPhotos({
  selectedAccount,
  username,
  accounts,
}: {
  selectedAccount?: string;
  username?: string;
  accounts?: InstagramAccountInfo[];
}) {
  if (selectedAccount === "all" && accounts && accounts.length > 1) {
    const photos = accounts;
    return (
      <div className="flex flex-shrink-0 -space-x-3" aria-label="Fotos das contas">
        {photos.map((account) => (
          <InstagramAvatar
            key={account.key}
            accountKey={account.key}
            alt={`@${account.username}`}
            className="h-12 w-12 text-sm ring-2 ring-white shadow-sm"
          />
        ))}
      </div>
    );
  }

  const singleKey =
    selectedAccount && selectedAccount !== "all" ? selectedAccount : accounts?.[0]?.key;
  if (!singleKey) {
    return (
      <span className="inline-flex h-14 w-14 flex-shrink-0 items-center justify-center rounded-full bg-slate-200 text-base font-semibold text-slate-500 ring-2 ring-white">
        ?
      </span>
    );
  }

  return (
    <InstagramAvatar
      accountKey={singleKey}
      alt={username ? `@${username}` : "Conta Instagram"}
      className="h-14 w-14 text-base ring-2 ring-white shadow-sm"
    />
  );
}

export function MarketingMetricsTabs({
  kpis,
  followers,
  indicators,
  published,
  loading,
  username,
  accounts,
  selectedAccount,
}: Props) {
  const [tab, setTab] = useState<MetricsTab>("account");
  const activeHint = TABS.find((t) => t.id === tab)?.hint ?? "";

  const delta = followers?.delta;
  const hasDelta = delta !== null && delta !== undefined;
  const isPositive = hasDelta && delta >= 0;
  const posts = published?.postsCount ?? 0;

  const interactions =
    kpis
      ? kpis.likes + kpis.comments + kpis.shares + kpis.saves + (kpis.replies ?? 0)
      : indicators?.interactions ?? 0;

  return (
    <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-card">
      <div className="flex flex-wrap items-end justify-between gap-4 border-b border-slate-100 pb-4">
        <div className="flex items-center gap-4">
          <AccountPhotos
            selectedAccount={selectedAccount}
            username={username}
            accounts={accounts}
          />
          <div>
            {username && selectedAccount !== "all" && (
              <p className="text-sm font-semibold text-slate-700">@{username}</p>
            )}
            <p className="text-xs font-semibold uppercase tracking-wider text-slate-500">
              Seguidores atuais
            </p>
            {loading ? (
              <div className="mt-2 flex h-10 items-center">
                <Loader size="sm" />
              </div>
            ) : (
              <p className="mt-1 text-3xl font-bold tabular-nums text-slate-800">
                {formatNumber(followers?.current ?? 0)}
              </p>
            )}
          </div>
        </div>
        {!loading && hasDelta && (
          <div
            className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-semibold ${
              isPositive ? "bg-emerald-50 text-emerald-700" : "bg-red-50 text-red-700"
            }`}
          >
            {isPositive ? (
              <TrendingUp className="h-3.5 w-3.5" />
            ) : (
              <TrendingDown className="h-3.5 w-3.5" />
            )}
            {isPositive ? "+" : ""}
            {formatNumber(delta)} seguidores líquidos
            {followers?.growthPct !== null && followers?.growthPct !== undefined && (
              <span className="opacity-80">
                ({isPositive ? "+" : ""}
                {formatPercent(followers.growthPct, 1)})
              </span>
            )}
          </div>
        )}
      </div>

      <div className="mt-4 flex gap-1 rounded-lg bg-slate-100 p-1">
        {TABS.map(({ id, label }) => (
          <button
            key={id}
            type="button"
            onClick={() => setTab(id)}
            className={cn(
              "flex-1 rounded-md px-3 py-2 text-xs font-semibold transition-colors sm:text-sm",
              tab === id
                ? "bg-white text-brand-700 shadow-sm"
                : "text-slate-500 hover:text-slate-700",
            )}
          >
            {label}
            {id === "published" && !loading && posts > 0 ? (
              <span className="ml-1.5 rounded-full bg-brand-100 px-1.5 py-0.5 text-[10px] text-brand-700">
                {posts}
              </span>
            ) : null}
          </button>
        ))}
      </div>

      <p className="mt-3 text-[11px] text-slate-400">{activeHint}</p>

      <div className="mt-4">
        {loading ? (
          <div className="flex h-24 items-center justify-center">
            <Loader size="sm" />
          </div>
        ) : tab === "account" ? (
          <div className="flex flex-col gap-5">
            <MetricsGrid items={ACCOUNT_METRICS} data={kpis ?? undefined} />
            <IndicatorCards
              variant="account"
              indicators={indicators}
              interactions={interactions}
            />
          </div>
        ) : posts === 0 ? (
          <div className="rounded-lg border border-slate-100 bg-slate-50 px-4 py-10 text-center text-sm text-slate-500">
            Nenhuma publicação no Feed ou Reels neste período.
          </div>
        ) : (
          <div className="flex flex-col gap-5">
            <MetricsGrid items={PUBLISHED_METRICS} data={published ?? undefined} />
            <IndicatorCards
              variant="published"
              indicators={indicators}
              interactions={interactions}
              published={published}
            />
          </div>
        )}
      </div>
    </div>
  );
}
